import {
    Box,
    Chip,
    ChipProps,
    LinearProgress,
    Paper,
    styled,
    Tooltip,
    Typography,
} from "@mui/material";
import { JSONViewer } from "components/UI/JSONViewer";
import React from "react";

/* ----------------------------- value helpers ----------------------------- */

export function shortId(value?: string | null): string {
    if (!value) return "—";
    return value.length > 8 ? value.slice(0, 8) : value;
}

export function formatDate(value?: string | null): string {
    if (!value) return "—";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export function formatNumber(value?: number | null): string {
    return typeof value === "number" ? value.toLocaleString() : "—";
}

/**
 * Filename-safe date-time stamp, e.g. "7-14-2026-141pm". Falls back to the
 * given fallback (typically the run id) when the value can't be parsed.
 */
export function formatFilenameDate(
    value?: string | null,
    fallback = "",
): string {
    if (!value) return fallback;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return fallback;
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    const hours24 = date.getHours();
    const meridiem = hours24 >= 12 ? "pm" : "am";
    const hours12 = hours24 % 12 || 12;
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${month}-${day}-${year}-${hours12}${minutes}${meridiem}`;
}

/** Human-readable duration between two ISO timestamps. */
export function formatDuration(start?: string | null, end?: string | null) {
    if (!start || !end) return "—";
    const ms = new Date(end).getTime() - new Date(start).getTime();
    if (Number.isNaN(ms) || ms < 0) return "—";
    if (ms < 1000) return `${ms}ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remSeconds}s`;
}

/** Coerce a value to something React can safely render. */
export function renderable(value: React.ReactNode): React.ReactNode {
    if (value === null || value === undefined || value === "") return "—";
    if (React.isValidElement(value)) return value;
    if (typeof value === "object") return JSON.stringify(value);
    return value;
}

/* ------------------------------- stat tile -------------------------------- */

const StatPaper = styled(Paper)(({ theme }) => ({
    alignItems: "flex-start",
    borderRadius: theme.shape.borderRadius / 2,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 120,
    padding: theme.spacing(1.5, 2),
    flex: "1 1 0",
}));

export function StatTile({
    label,
    value,
    accent,
    hint,
}: {
    label: string;
    value: React.ReactNode;
    accent?: string;
    hint?: string;
}) {
    const tile = (
        <StatPaper variant="outlined">
            <Typography
                variant="caption"
                sx={{ color: "text.secondary", textTransform: "uppercase" }}
            >
                {label}
            </Typography>
            <Typography
                variant="h6"
                sx={{ color: accent ?? "text.primary", fontWeight: 700 }}
            >
                {renderable(value)}
            </Typography>
        </StatPaper>
    );
    return hint ? (
        <Tooltip title={hint} arrow>
            {tile}
        </Tooltip>
    ) : (
        tile
    );
}

/* --------------------------- label / value row --------------------------- */

export function Field({
    label,
    value,
}: {
    label: string;
    value: React.ReactNode;
}) {
    return (
        <Box sx={{ display: "flex", gap: 1.5, py: 0.4 }}>
            <Typography
                variant="body2"
                sx={{ color: "text.secondary", flex: "0 0 160px" }}
            >
                {label}
            </Typography>
            <Typography
                variant="body2"
                sx={{ flex: 1, wordBreak: "break-word" }}
            >
                {renderable(value)}
            </Typography>
        </Box>
    );
}

/* --------------------------------- chips ---------------------------------- */

export function LabeledChip({
    label,
    icon,
    color = "default",
    variant = "outlined",
}: {
    label: React.ReactNode;
    icon?: React.ReactElement;
    color?: ChipProps["color"];
    variant?: ChipProps["variant"];
}) {
    return (
        <Chip
            size="small"
            color={color}
            variant={variant}
            icon={icon}
            label={label}
        />
    );
}

/** Renders a 0..max score as a labeled progress bar. */
export function ScoreBar({
    label,
    score,
    max = 3,
}: {
    label: string;
    score?: number;
    max?: number;
}) {
    const value = typeof score === "number" ? score : 0;
    const pct = Math.max(0, Math.min(100, (value / max) * 100));
    const color = pct >= 100 ? "success" : pct >= 50 ? "warning" : "error";
    return (
        <Box sx={{ minWidth: 160 }}>
            <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 0.25 }}
            >
                <Typography variant="caption" color="text.secondary">
                    {label}
                </Typography>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {typeof score === "number" ? `${score}/${max}` : "—"}
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={pct}
                color={color}
                sx={{ borderRadius: 1, height: 6 }}
            />
        </Box>
    );
}

/* ------------------------------- json card -------------------------------- */

/** Titled card wrapping a rich JSON view; renders nothing when there is no data. */
export function JsonCard({
    title,
    data,
}: {
    title: string;
    data: unknown;
}) {
    const isEmpty =
        data === null ||
        data === undefined ||
        (Array.isArray(data) && data.length === 0) ||
        (typeof data === "object" && Object.keys(data as object).length === 0);
    return (
        <Paper variant="outlined" sx={{ overflow: "hidden", width: "100%" }}>
            <Box
                sx={{
                    backgroundColor: "gray.100",
                    px: 1.5,
                    py: 1,
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
            </Box>
            {isEmpty ? (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ p: 1.5 }}
                >
                    No data.
                </Typography>
            ) : (
                <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                    <JSONViewer value={data} display="rich" />
                </Box>
            )}
        </Paper>
    );
}

/* ----------------------------- section frame ------------------------------ */

export const SectionStack = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    width: "100%",
}));

export function SectionHeading({
    children,
    count,
}: {
    children: React.ReactNode;
    count?: number;
}) {
    return (
        <Box sx={{ alignItems: "center", display: "flex", gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {children}
            </Typography>
            {count !== undefined && (
                <Chip size="small" label={count} variant="outlined" />
            )}
        </Box>
    );
}
